<template>
	<NP-Table class="cashdesk-shifts-table" :data="cashdesksList" :columns="columns" :options="options" hide-controls>
		<template #signaturesIdentifier="{ slotProps: { row } }">
			<NP-Tooltip :text="row.has_signature_requests ? 'Не проставлені ЕЦП' : undefined">
				<div :class="{ 'icon-key-wrap': true, active: row.has_signature_requests }">
					<i class="icon-key"></i>
				</div>
			</NP-Tooltip>
		</template>
		<template #forceClose="{ slotProps: { row } }">
			<NP-Button v-if="!row.is_chiefs_row" variant="square" @click.stop="() => toggleClosalShiftModal(row)">
				<div class="icon-exit" slot="icon" />
			</NP-Button>
		</template>
		<template #rowActions="{ slotProps: { row } }">
			<span v-if="row.anyOrderExists" class="order-balance">
				{{ row.anyOrderExists.recorded_amount | formatMoney }}
			</span>
			<NP-Input
				v-else
				type="monetary"
				placeholder="0.00"
				v-model="balances[row.id]"
				@keydown.enter.prevent="() => balances[row.id] && onConfirm(row)"
			/>
			<NP-Button v-if="row.anyOrderExists" variant="square" @click.stop="() => onReject(row)">
				<div class="icon-close" slot="icon" />
			</NP-Button>
			<NP-Button
				v-if="!row.cashToCashdesk || row.isOrderToChiefCashierCashdesk"
				variant="square"
				@click.stop="() => onConfirm(row)"
				:disabled="!balances[row.id] && !row.anyOrderExists"
				:priority="row.cashFromCashdesk ? 'high' : 'medium'"
			>
				<div class="icon-confirm-white" slot="icon" />
			</NP-Button>
		</template>
	</NP-Table>
</template>

<script>
import BigNumber from 'bignumber.js';
import isEqual from 'lodash/isEqual';
import find from 'lodash/find';

import { enum as orderStatuses } from '@repo/enums/cashbook-order-statuses';
import { enum as orderTypes } from '@repo/enums/cashbook-order-types';
import { enum as orderSubTypes } from '@repo/enums/cashbook-order-sub-types';
import cashdeskActionTypes from './store/cashdesk-action-types';

export default {
	name: 'CashdesksTable',
	props: {
		user: { type: Object, default: null },
		root: { type: Object, default: null },
		cashdesks: { type: Array, default: () => [] },
		pendingOrders: { type: Array, default: () => [] },
		toggleClosalShiftModal: { type: Function, default: null },
		toggleCashdeskActionModal: { type: Function, default: null },
		togglePendingOrdersModal: { type: Function, default: null },
		searchQuery: { type: String, default: '' }
	},
	data() {
		return {
			balances: {}
		};
	},
	computed: {
		cashdesksList() {
			return this.cashdesks
				.map((c) => {
					let orders = this.pendingOrders.filter((o) => o.participant_id === c.user_id);
					let cashToCashdesk = orders.find(
						(o) => o.status === orderStatuses.created && o.sub_type === orderSubTypes.cashToCashdesk
					);
					let cashFromCashdesk = orders.find(
						(o) => o.status === orderStatuses.created && o.sub_type === orderSubTypes.cashFromCashdesk
					);

					let cashToCourier = !!orders.find(
						(o) => o.status === orderStatuses.confirmed && o.sub_type === orderSubTypes.cashToCourier
					);
					let isOrderToChiefCashierCashdesk = cashToCashdesk && cashToCashdesk.participant_id === this.user.id;
					let balance = new BigNumber(c.in_balance).minus(c.out_balance).toNumber();
					let pending_courier_balance =
						cashToCourier &&
						new BigNumber(
							orders
								.filter((o) => o.sub_type === orderSubTypes.cashToCourier)
								.reduce((acc, curr) => {
									return new BigNumber(acc).plus(curr.factual_amount).toNumber();
								}, 0)
						).toNumber();
					return {
						...c,
						balance,
						cashToCashdesk,
						cashFromCashdesk,
						cashToCourier,
						isOrderToChiefCashierCashdesk,
						anyOrderExists: cashToCashdesk || cashFromCashdesk,
						pending_courier_balance,
						is_chiefs_row: this.root.cashbook_shift && this.root.cashbook_shift.user_id === c.user_id,
						has_signature_requests: (this.root.signatureRequests || []).filter(
							(r) => r.user_id === c.user_id || (r.signerUserIds || []).includes(c.user_id)
						).length
					};
				})
				.filter((c) =>
					[c.user_last_name, c.user_first_name, c.cash_machine_code].some(
						(val) => val && val.toLowerCase().includes(this.searchQuery.toLowerCase())
					)
				)
				.sort((a, b) => (b.anyOrderExists ? 1 : -1));
		},
		columns() {
			return [
				{
					key: 'signaturesIdentifier',
					slotName: 'signaturesIdentifier',
					type: 'slot',
					class: 'signatures-identifier'
				},
				{
					key: 'user',
					label: 'ПІБ касира',
					class: 'cashier-name',
					format: (_, row) => `${row.user_last_name} ${row.user_first_name}${row.is_chiefs_row ? ' (Ви)' : ''}`
				},
				{
					key: 'cash_machine_code',
					class: 'cash-machine-code-cell',
					format: (val, row) => {
						let substr = '';
						if (row.cashToCashdesk) {
							substr = `ВКО ${val ? 'для' : ''}`;
						}
						if (row.cashFromCashdesk) {
							substr = `ПКО ${val ? 'від' : ''}`;
						}
						return `${substr} ${val || ''}`;
					}
				},
				{
					type: 'amount',
					key: 'pending_courier_balance',
					label: 'Переміщення розмін'
				},
				{
					type: 'slot',
					slotName: 'forceClose',
					class: 'force-close-button-cell',
					label: 'Закриття'
				},
				{
					type: 'amount',
					key: 'balance',
					label: 'Баланс, готівка'
				},
				{
					type: 'slot',
					slotName: 'rowActions',
					class: 'row-actions',
					label: 'Сума внесення/видачі'
				}
			];
		},
		options() {
			return {
				getRowClass: ({ raw }) => {
					let { cashToCashdesk, cashFromCashdesk } = raw;
					if (cashToCashdesk) {
						return 'cash-to-cashdesk';
					}
					if (cashFromCashdesk) {
						return 'cash-from-cashdesk';
					}
				}
			};
		}
	},
	methods: {
		onConfirm(cashdesk) {
			let { cashToCashdesk, cashFromCashdesk, isOrderToChiefCashierCashdesk, anyOrderExists, ...data } = cashdesk;
			let balance = this.balances[cashdesk.id];
			let hasPendingOrders = cashdesk?.pending_courier_balance && this.root?.pending_cashbook_orders;

			if (hasPendingOrders) {
				this.togglePendingOrdersModal(data);
				return;
			}
			if (cashFromCashdesk) {
				this.toggleCashdeskActionModal({
					cashdesk: data,
					order: cashFromCashdesk,
					action: cashdeskActionTypes.confirm
				});
			}
			if (cashToCashdesk && isOrderToChiefCashierCashdesk) {
				this.toggleCashdeskActionModal({
					cashdesk: data,
					order: cashToCashdesk,
					action: cashdeskActionTypes.confirm
				});
			}
			if (!anyOrderExists) {
				this.toggleCashdeskActionModal({
					cashdesk: data,
					order: {
						recorded_amount: balance,
						type: orderTypes.outgoing,
						sub_type: orderSubTypes.cashToCashdesk
					},
					action: cashdeskActionTypes.create
				});
			}
		},
		onReject(cashdesk) {
			//eslint-disable-next-line
			let { cashToCashdesk, cashFromCashdesk, isOrderToChiefCashierCashdesk, anyOrderExists, ...data } = cashdesk;
			if (anyOrderExists) {
				this.balances = {
					...this.balances,
					[cashdesk.id]: ''
				};
				this.toggleCashdeskActionModal({
					cashdesk: data,
					order: anyOrderExists,
					action: cashdeskActionTypes.cancel
				});
			}
		}
	},
	watch: {
		cashdesks: {
			handler(newVal, oldVal) {
				let [changedRow] = newVal.filter((c) => {
					let updated = find(oldVal, { id: c.id });
					return updated && !isEqual(updated, c);
				});
				if (changedRow) {
					this.balances = {
						...this.balances,
						[changedRow.id]: ''
					};
				}
			},
			deep: true
		}
	}
};
</script>

<style lang="scss" scoped>
@import '@assets/scss/variables.scss';
@import '@assets/scss/mixins.scss';

$icon-close: '~@assets/images/icon-close.svg';
$icon-confirm: '~@assets/images/icon-confirm.svg';
$icon-logout: '~@assets/images/icon-logout.svg';
$icon-key: '~@assets/images/icon-key.svg';

.cashdesk-shifts-table {
	::v-deep {
		.table table {
			border-collapse: separate;
			border-spacing: 0px;
			.no-data {
				display: none;
			}
			thead th.column-header {
				padding: 8px 10px;
				.column-header-container .header-label {
					white-space: nowrap;
					font-family: Proxima Nova Semibold;
				}
				&.slot.force-close-button-cell {
					.column-header-container {
						justify-content: flex-end;
					}
				}
			}
			tbody tr {
				td {
					height: 44px;
					background: $white;
					border: 1px solid $white;
					border-right: none;
					border-left: none;
					&.cell {
						border-top: none;
						border-bottom: none;
						button.button.square {
							height: 36px;
							min-width: 36px;
							max-width: 36px;
							margin-right: 4px;
							margin-left: 4px;
						}
						.icon-exit {
							@include make-icon($icon-logout, $gray-50, 20px, 20px);
						}
						.icon-close {
							@include make-icon($icon-close, $gray-50, 20px, 20px);
						}
						.icon-confirm-white {
							@include make-icon($icon-confirm, $white, 20px, 20px);
						}
						&.signatures-identifier {
							padding: 0 0 0 5px;
							.icon-key-wrap {
								display: flex;
								justify-content: center;
								align-items: center;
								width: 20px;
								height: 20px;
								i.icon-key {
									@include make-icon($icon-key, $gray-40, 10px, 10px);
								}
								&.active {
									border-radius: 50%;
									background: $pink;
									i.icon-key {
										@include make-icon($icon-key, $red, 10px, 10px);
									}
								}
							}
						}
						&.text.cash-machine-code-cell {
							white-space: nowrap;
							text-align: right;
							min-width: 160px;
							max-width: 160px;
						}
						&.text.cashier-name {
							white-space: nowrap;
						}
						&.amount {
							min-width: 137px;
							@include small-desktop() {
								min-width: 113px;
							}
							&::before {
								content: '₴';
								font-family: Montserrat;
							}
						}
						&.force-close-button-cell {
							width: 100%;
							text-align: right;
						}
						&.row-actions {
							width: 250px;
							display: flex;
							align-items: center;
							justify-content: space-between;
							padding: 4px 0 4px 8px;
							.input.monetary.gray-30 {
								width: 194px;
								span.uah {
									top: 9.5px;
								}
								input {
									padding-left: 28px;
									height: 36px;
									font-size: 14px;
								}
								&.focused {
									input {
										padding-left: 27px;
									}
								}
							}
							.order-balance {
								width: 190px;
								&::before {
									content: '₴';
									font-family: Montserrat;
								}
							}
						}
					}
					&:first-child {
						border-top-left-radius: 4px;
						border-bottom-left-radius: 4px;
						border-right: 1px solid $white;
					}
					&:last-child {
						border-top-right-radius: 4px;
						border-bottom-right-radius: 4px;
						border-left: 1px solid $white;
					}
				}
				&:nth-child(odd) {
					td {
						background: $white;
					}
				}
				&:hover {
					td {
						background: $gray-5;
						border-right: none;
						border-left: none;
						&:first-child {
							border-right: 1px solid $gray-5;
						}
						&:last-child {
							border-left: 1px solid $gray-5;
						}
					}
				}
				&.cash-to-cashdesk {
					td {
						background: #fff4db;
						&:first-child {
							border-right: 1px solid #fff4db;
						}
						&:last-child {
							border-left: 1px solid #fff4db;
						}
						&.cell.text.cash-machine-code-cell {
							color: #e0a200;
						}
					}
				}
				&.cash-from-cashdesk {
					td {
						background: #fde8e9;
						&:first-child {
							border-right: 1px solid #fde8e9;
						}
						&:last-child {
							border-left: 1px solid #fde8e9;
						}
						&.cell.text.cash-machine-code-cell {
							color: $red;
						}
					}
				}
			}
		}
	}
}
</style>
